/**
 * Binds the Supabase session to the app's session store.
 *
 * The store is what `lib/api-client` reads when it attaches the bearer token, so
 * everything downstream stays a plain read of client state.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { clearSession, setSession, useSession } from '../store/session'

/** Mount once, at the shell. Keeps the store in step with token refreshes. */
export function useAuthListener(): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      setSession({
        accessToken: data.session?.access_token ?? null,
        userId: data.session?.user.id ?? null,
      })
    })

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession({
        accessToken: session?.access_token ?? null,
        userId: session?.user.id ?? null,
      })
      // A different user must never see the previous one's cached farm data.
      if (!session) queryClient.clear()
    })

    return () => subscription.subscription.unsubscribe()
  }, [queryClient])
}

export function useSignIn() {
  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const { data, error } = await supabase.auth.signInWithPassword(credentials)
      if (error) throw new Error(error.message)
      return data
    },
  })
}

export function useSignOut() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await supabase.auth.signOut()
      clearSession()
      queryClient.clear()
    },
  })
}

export function useIsSignedIn(): boolean {
  return useSession().accessToken !== null
}
